import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createHoliday,
  deleteHoliday,
  getHolidays,
  syncHolidays,
  updateHoliday,
} from "../../../apis/holidays.api";
import { AppAlertBox } from "../../../components/modals/alert-box";
import FlexBox from "../../../components/layouts/flexbox";
import { showNotification } from "../../../services/utils";
import { useStyles } from "../styles";
import { HolidayFormModal } from "./holiday-form-modal";
import { HOLIDAY_COUNTRY_CODE, HOLIDAY_TYPES, Holiday } from "./types";
import { formatHolidayDate } from "./utils";

function HolidaySetup() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();

  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState("");
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Holiday | null>(null);

  const loadHolidays = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const res: any = await dispatch(
        getHolidays({
          year: currentYear,
          countryCode: HOLIDAY_COUNTRY_CODE,
          limit: 500,
        })
      );
      if (res?.success) {
        setHolidays(Array.isArray(res.data) ? res.data : []);
      } else {
        setHolidays([]);
        setError("Failed to load holidays. Please try again.");
      }
    } catch (err) {
      setHolidays([]);
      setError("Failed to load holidays. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, currentYear]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  const handleOpenAdd = () => {
    setEditingHoliday(null);
    setShowFormModal(true);
  };

  const handleOpenEdit = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setShowFormModal(true);
  };

  const handleCloseForm = () => {
    setShowFormModal(false);
    setEditingHoliday(null);
  };

  const handleSubmit = async (payload: Record<string, any>) => {
    const response: any = editingHoliday
      ? await dispatch(updateHoliday(editingHoliday._id, payload))
      : await dispatch(createHoliday(payload));

    if (response?.success) {
      handleCloseForm();
      await loadHolidays();
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const response: any = await dispatch(
        syncHolidays({ countryCode: HOLIDAY_COUNTRY_CODE })
      );
      if (response?.success) {
        const data = response.data || {};
        showNotification({
          message: `Synced holidays (created: ${data.created || 0}, updated: ${
            data.updated || 0
          }, skipped: ${data.skipped || 0})`,
          type: "success",
        });
        await loadHolidays();
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?._id) return;
    const response: any = await dispatch(deleteHoliday(deleteTarget._id));
    setDeleteTarget(null);
    if (response?.success) {
      await loadHolidays();
    }
  };

  return (
    <Paper
      variant="outlined"
      className={classes.categoryCard}
      component="section"
      aria-labelledby="app-settings-holiday-setup-heading"
    >
      <Typography
        variant="h6"
        mb={2}
        component="h2"
        id="app-settings-holiday-setup-heading"
        className={classes.cardTitle}
      >
        Holiday Setup
      </Typography>

      <FlexBox
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        mb={2}
      >
        <Typography fontSize={13} color="text.secondary">
          Year: <strong>{currentYear}</strong>
        </Typography>

        <FlexBox gap={1.5} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={handleSync}
            disabled={isSyncing || isLoading}
            sx={{ textTransform: "none" }}
          >
            {isSyncing ? "Syncing…" : "Sync holidays"}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{ textTransform: "none" }}
            disableElevation
          >
            Add holiday
          </Button>
        </FlexBox>
      </FlexBox>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Typography color="error" fontSize={13} py={2}>
          {error}
        </Typography>
      ) : holidays.length === 0 ? (
        <Typography fontSize={13} color="text.secondary" py={2}>
          No holidays found. Sync holidays or add one manually.
        </Typography>
      ) : (
        <TableContainer
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            maxHeight: 420,
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Date</TableCell>
                <TableCell sx={headerCellSx}>Name</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell align="right" sx={{ ...headerCellSx, width: 100 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {holidays.map((holiday) => (
                <TableRow key={holiday._id}>
                  <TableCell>
                    <FlexBox flexDirection="column" alignItems="flex-start" gap={0.5}>
                      <Typography
                        component="span"
                        fontSize={13}
                        whiteSpace="nowrap"
                      >
                        {formatHolidayDate(holiday, currentYear)}
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          holiday.type === HOLIDAY_TYPES.ANNUAL
                            ? "Annual"
                            : "One-time"
                        }
                        color={
                          holiday.type === HOLIDAY_TYPES.ANNUAL
                            ? "primary"
                            : "secondary"
                        }
                        variant="outlined"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 500,
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    </FlexBox>
                  </TableCell>
                  <TableCell>{holiday.name}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={holiday.active === false ? "Inactive" : "Active"}
                      color={holiday.active === false ? "default" : "success"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={`Edit ${holiday.name}`}
                      onClick={() => handleOpenEdit(holiday)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={`Delete ${holiday.name}`}
                      onClick={() => setDeleteTarget(holiday)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {showFormModal && (
        <HolidayFormModal
          holiday={editingHoliday}
          onClose={handleCloseForm}
          onSubmit={handleSubmit}
        />
      )}

      {deleteTarget && (
        <AppAlertBox
          isModalOpen={true}
          handleClose={() => setDeleteTarget(null)}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteTarget(null)}
          message={
            <Box>
              <Typography fontWeight={600} mb={1.5}>
                Delete holiday?
              </Typography>
              <Typography fontSize="14px" color="#6B7280" lineHeight={1.6}>
                This will permanently remove{" "}
                <strong>{deleteTarget.name}</strong>.
              </Typography>
            </Box>
          }
          maxModalWidth="sm"
        />
      )}
    </Paper>
  );
}

const headerCellSx = {
  fontWeight: 600,
  backgroundColor: "#f5f5f5",
};

export { HolidaySetup };
